import { RoleModel, UserRoleModel } from "@/types/models";
import { privateApi } from "./api";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const getRoles = async (projectId: string) => {
    return await privateApi.get<RoleModel[]>(`/project/${projectId}/roles`);
};

const getUserRoles = async (projectId: string) => {
    return await privateApi.get<UserRoleModel[]>(`/project/${projectId}/roles/userRoles`);
};

const assignRolesToUser = async ({ 
    projectId, 
    userId, 
    roleId 
}: { 
    projectId: string; 
    userId: string; 
    roleId: string; 
}) => {
    return await privateApi.post(`/project/${projectId}/roles/assignRoles`, {
        userId,
        roleIds: [roleId]
    });
};

const addUserToProject = async ({ 
    projectId, 
    userEmail, 
    roleId 
}: { 
    projectId: string; 
    userEmail: string; 
    roleId: string; 
}) => {
    return await privateApi.post(`/project/${projectId}/roles/addUser`, {
        userEmail,
        roleId
    });
};

const removeUserRoles = async ({ 
    projectId, 
    userId 
}: { 
    projectId: string; 
    userId: string; 
}) => {
    return await privateApi.delete(`/project/${projectId}/roles`, {
        data: { userId }
    });
};

// Hooks
export const useRoles = (projectId?: string) => {
    return useQuery({
        queryKey: ['roles', projectId],
        queryFn: () => {
            if (!projectId) return Promise.reject("Project ID missing");
            return getRoles(projectId).then((response) => response.data);
        },
        enabled: !!projectId,
    });
};

export const useUserRoles = (projectId?: string) => {
    return useQuery({
        queryKey: ['userRoles', projectId],
        queryFn: () => {
            if (!projectId) return Promise.reject("Project ID missing");
            return getUserRoles(projectId).then((response) => response.data);
        },
        enabled: !!projectId,
    });
};

export const useAssignRolesToUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: assignRolesToUser,
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: ['userRoles', projectId] });
        },
        onError: (error) => {
            console.error("Error while assigning roles", error);
            throw error;
        },
    });
};

export const useAddUserToProject = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: addUserToProject,
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: ['userRoles', projectId] });
        },
        onError: (error) => {
            throw error;
        },
    });
};

export const useRemoveUserRoles = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: removeUserRoles,
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: ['userRoles', projectId] });
        },
        onError: (error) => {
            console.error("Error while removing user roles", error);
            throw error;
        },
    });
};